import { useQuery } from '@tanstack/react-query';
import type { LigneVenteInput, SortieStockInput } from '@cockpit/shared';
import { api } from '../../lib/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { formatEuro, formatM2, formatPct } from '../../lib/format';
import { RouleauPicker } from '../stock/RouleauPicker';

interface Props {
  lignes: LigneVenteInput[];
  onChange: (lignes: LigneVenteInput[]) => void;
}

// Calcul local pour affichage (autorité = serveur).
function calcLigneLocale(l: LigneVenteInput): {
  ht: number;
  tva: number;
  ttc: number;
  marge: number | null;
} {
  const brutX100 = l.m2_centi * l.prix_unitaire_centimes_par_m2;
  const apresRemiseX100 = (brutX100 * (10000 - l.remise_ligne_pct)) / 10000;
  const ht = Math.round(apresRemiseX100 / 100);
  const tva = Math.round((ht * l.taux_tva) / 10000);
  const ttc = ht + tva;
  const cout = l.cout_unitaire_centimes_par_m2 ?? l.cout_unitaire_estime_centimes_par_m2;
  const marge = cout !== null ? ht - Math.round((l.m2_centi * cout) / 100) : null;
  return { ht, tva, ttc, marge };
}

function emptyLigne(ordre: number): LigneVenteInput {
  return {
    reference_id: null,
    reference_libre: '',
    largeur_cm: null,
    longueur_cm: null,
    m2_centi: 0,
    nb_rouleaux_entames: null,
    prix_unitaire_centimes_par_m2: 0,
    remise_ligne_pct: 0,
    taux_tva: 2000,
    cout_unitaire_centimes_par_m2: null,
    cout_unitaire_estime_centimes_par_m2: null,
    ordre,
  };
}

function parseIntegerCentimes(input: string, decimalFactor: number): number {
  const cleaned = input.replace(/\s/g, '').replace(/,/g, '.');
  const f = Number.parseFloat(cleaned);
  if (!Number.isFinite(f) || f < 0) return 0;
  return Math.round(f * decimalFactor);
}

export function LignesVenteEditor({ lignes, onChange }: Props): JSX.Element {
  const refsQuery = useQuery({
    queryKey: ['references', 'all'],
    queryFn: () => api.references.list(),
  });
  const references = refsQuery.data?.items ?? [];

  const updateLigne = (idx: number, patch: Partial<LigneVenteInput>): void => {
    const next = lignes.map((l, i) => (i === idx ? { ...l, ...patch } : l));
    onChange(next);
  };

  const handleReferenceChange = (idx: number, refIdRaw: string): void => {
    if (refIdRaw === '') {
      updateLigne(idx, { reference_id: null, sorties: undefined });
      return;
    }
    const refId = Number.parseInt(refIdRaw, 10);
    const r = references.find((x) => x.id === refId);
    updateLigne(idx, {
      reference_id: refId,
      reference_libre: r?.nom ?? '',
      sorties: [],
    });
  };

  const handleSortiesChange = (idx: number, sorties: SortieStockInput[]): void => {
    updateLigne(idx, { sorties });
  };

  const addLigne = (): void => {
    onChange([...lignes, emptyLigne(lignes.length)]);
  };

  const removeLigne = (idx: number): void => {
    const next = lignes.filter((_, i) => i !== idx).map((l, i) => ({ ...l, ordre: i }));
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-200">Lignes produit</h3>
        <Button type="button" variant="secondary" size="sm" onClick={addLigne}>
          + Ajouter une ligne
        </Button>
      </div>

      {lignes.length === 0 && (
        <div className="text-xs text-slate-500 border border-dashed border-slate-700 rounded-md p-4 text-center">
          Aucune ligne. Cliquer sur "+ Ajouter une ligne" pour commencer.
        </div>
      )}

      {lignes.map((ligne, idx) => {
        const calc = calcLigneLocale(ligne);
        return (
          <div
            key={idx}
            className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 flex flex-col gap-3"
          >
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500">Ligne {String(idx + 1)}</div>
              <button
                type="button"
                onClick={(): void => removeLigne(idx)}
                className="text-xs text-red-400 hover:text-red-300"
              >
                Supprimer
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Select
                label="Référence catalogue"
                value={ligne.reference_id === null ? '' : String(ligne.reference_id)}
                onChange={(e): void => handleReferenceChange(idx, e.target.value)}
                options={[
                  { value: '', label: '— Aucune (saisie libre) —' },
                  ...references.map((r) => ({ value: String(r.id), label: r.nom })),
                ]}
              />
              <Input
                label="Désignation libre (si pas de référence)"
                value={ligne.reference_libre ?? ''}
                onChange={(e): void => updateLigne(idx, { reference_libre: e.target.value })}
                placeholder="Ex : Gazon Premium 40mm"
                disabled={ligne.reference_id !== null}
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Select
                label="Largeur (cm)"
                value={ligne.largeur_cm === null ? '' : String(ligne.largeur_cm)}
                onChange={(e): void =>
                  updateLigne(idx, {
                    largeur_cm:
                      e.target.value === '' ? null : (Number(e.target.value) as 200 | 400),
                  })
                }
                options={[
                  { value: '200', label: '200 cm' },
                  { value: '400', label: '400 cm' },
                ]}
                placeholder="—"
              />
              <Input
                label="Longueur totale (m)"
                type="text"
                value={ligne.longueur_cm === null ? '' : String(ligne.longueur_cm / 100)}
                onChange={(e): void => {
                  const v = e.target.value.replace(',', '.');
                  if (v === '') {
                    updateLigne(idx, { longueur_cm: null });
                    return;
                  }
                  const f = Number.parseFloat(v);
                  updateLigne(idx, {
                    longueur_cm: Number.isFinite(f) ? Math.round(f * 100) : null,
                  });
                }}
              />
              <Input
                label="Surface (m²) *"
                type="text"
                value={String(ligne.m2_centi / 100)}
                onChange={(e): void =>
                  updateLigne(idx, { m2_centi: parseIntegerCentimes(e.target.value, 100) })
                }
                hint={`= ${formatM2(ligne.m2_centi)}`}
              />
              <Input
                label="Nb rouleaux entamés"
                type="text"
                value={ligne.nb_rouleaux_entames === null ? '' : String(ligne.nb_rouleaux_entames)}
                onChange={(e): void => {
                  const f = Number.parseInt(e.target.value, 10);
                  updateLigne(idx, {
                    nb_rouleaux_entames: Number.isFinite(f) && f > 0 ? f : null,
                  });
                }}
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Input
                label="Prix unitaire (€ HT / m²) *"
                type="text"
                value={String(ligne.prix_unitaire_centimes_par_m2 / 100)}
                onChange={(e): void =>
                  updateLigne(idx, {
                    prix_unitaire_centimes_par_m2: parseIntegerCentimes(e.target.value, 100),
                  })
                }
                hint={`= ${formatEuro(ligne.prix_unitaire_centimes_par_m2)} / m²`}
              />
              <Input
                label="Remise ligne (%)"
                type="text"
                value={String(ligne.remise_ligne_pct / 100)}
                onChange={(e): void =>
                  updateLigne(idx, { remise_ligne_pct: parseIntegerCentimes(e.target.value, 100) })
                }
                hint={`= ${formatPct(ligne.remise_ligne_pct)}`}
              />
              <Input
                label="TVA (%)"
                type="text"
                value={String(ligne.taux_tva / 100)}
                onChange={(e): void =>
                  updateLigne(idx, { taux_tva: parseIntegerCentimes(e.target.value, 100) })
                }
                hint={`= ${formatPct(ligne.taux_tva)}`}
              />
              <Input
                label="Coût estimé (€ / m²) — historique"
                type="text"
                value={
                  ligne.cout_unitaire_estime_centimes_par_m2 === null
                    ? ''
                    : String(ligne.cout_unitaire_estime_centimes_par_m2 / 100)
                }
                onChange={(e): void => {
                  if (e.target.value === '') {
                    updateLigne(idx, { cout_unitaire_estime_centimes_par_m2: null });
                  } else {
                    updateLigne(idx, {
                      cout_unitaire_estime_centimes_par_m2: parseIntegerCentimes(
                        e.target.value,
                        100,
                      ),
                    });
                  }
                }}
                hint="Si pas de référence catalogue. Locké si rouleaux sélectionnés."
                disabled={ligne.reference_id !== null}
              />
            </div>

            {ligne.reference_id !== null && (
              <RouleauPicker
                referenceId={ligne.reference_id}
                largeurCm={
                  ligne.largeur_cm === 200 || ligne.largeur_cm === 400 ? ligne.largeur_cm : null
                }
                m2CentiTarget={ligne.m2_centi}
                value={ligne.sorties ?? []}
                onChange={(s): void => handleSortiesChange(idx, s)}
              />
            )}

            <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-slate-800 text-xs text-slate-400">
              <span>
                HT (calcul local) : <span className="text-slate-100">{formatEuro(calc.ht)}</span>
              </span>
              <span>
                TVA : <span className="text-slate-100">{formatEuro(calc.tva)}</span>
              </span>
              <span>
                TTC : <span className="text-slate-100">{formatEuro(calc.ttc)}</span>
              </span>
              <span>
                Marge : <span className="text-slate-100">{formatEuro(calc.marge)}</span>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
